import { useState } from "react";

import errorImages from "common/constants/errorImages";
import { isHit } from "common/utils/strings";
import { useNavigate } from "common/router/hooks";
import { Optional } from "common/utils/types";

import PillSearchInput from "client/common/components/inputs/PillSearchInput";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { urls } from "client/routes";

import { DosageReminder, DosageReminderPatientItem } from "../types";
import { useDosageReminderRecentItemsQuery, useDosageRemindersQuery } from "../hooks";
import { useReminderDrawer } from "../stackables";
import CreateItemCard from "./CreateItemCard";
import ReminderDrawerLayout from "./ReminderDrawerLayout";

import styles from "../styles/ReminderItemSelectDrawer.module.less";

const MIN_ITEMS_FOR_SEARCH = 5;

type RecentItemSelection = {
  patientItem: DosageReminderPatientItem;
  reminder: Optional<DosageReminder>;
};

const getReminderByPatientItem = (reminders: DosageReminder[]) =>
  reminders.reduce((reminderByPatientItem, reminder) => {
    const key = `${reminder.patient.id}-${reminder.item.id}`;
    const existing = reminderByPatientItem.get(key);

    // Show active Reminder if present, otherwise the first Reminder
    if (!existing || (!existing.is_active && reminder.is_active)) {
      reminderByPatientItem.set(key, reminder);
    }
    return reminderByPatientItem;
  }, new Map<string, DosageReminder>());

const isSearchHit = (search: string, { patient, item }: DosageReminderPatientItem) =>
  [item.product.product_line.name, item.product.name, item.name, patient.name].some((value) =>
    isHit(search, value),
  );

// Sorts Items without Reminders first, then Items with Active Reminders, then with Inactive Reminders
const getReminderSortRank = (reminder: Optional<DosageReminder>) => {
  if (!reminder) {
    return 0;
  }
  if (reminder.is_active) {
    return 1;
  }
  return 2;
};

type GetSearchResultsParams = {
  patientItems: DosageReminderPatientItem[];
  reminders: DosageReminder[];
  search: string;
};

const getSearchResults = ({ patientItems, reminders, search }: GetSearchResultsParams) => {
  const reminderByPatientItem = getReminderByPatientItem(reminders);
  return patientItems
    .filter((patientItem) => isSearchHit(search, patientItem))
    .map((patientItem) => {
      const key = `${patientItem.patient.id}-${patientItem.item.id}`;
      return { key, patientItem, reminder: reminderByPatientItem.get(key) };
    })
    .sort((a, b) => getReminderSortRank(a.reminder) - getReminderSortRank(b.reminder));
};

const ReminderItemSelectDrawer = () => {
  const { close } = useCurrentDrawer();
  const reminderDrawer = useReminderDrawer();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const { data: patientItems, isLoading, error } = useDosageReminderRecentItemsQuery();
  const {
    data: reminders,
    isLoading: isLoadingReminders,
    error: remindersError,
  } = useDosageRemindersQuery();

  const handleSelect = ({ patientItem, reminder }: RecentItemSelection) => {
    close();
    reminderDrawer(
      reminder
        ? { reminder, canGoBack: true }
        : { patient: patientItem.patient, item: patientItem.item },
    ).open();
  };

  const handleSearchProducts = () => {
    close();
    navigate(urls.products.search());
  };

  if (isLoading || isLoadingReminders) {
    return <DrawerLayout.Loading />;
  }
  if (error || remindersError || !patientItems || !reminders) {
    return (
      <DrawerLayout.FetchError
        entityName="recent medications"
        error={error ?? remindersError}
        isPlural={true}
      />
    );
  }
  if (!patientItems.length) {
    return (
      <ReminderDrawerLayout>
        <DrawerLayout.Error
          title="No recent medications"
          description="Medications from your orders in the last year will show up here"
          image={errorImages.EmptyResults}
          imageAlt="Nothing found"
          actions={[{ label: "Search products", onClick: handleSearchProducts }]}
        />
      </ReminderDrawerLayout>
    );
  }

  const searchResults = getSearchResults({ patientItems, reminders, search });

  return (
    <ReminderDrawerLayout description="Select an item from your recent orders to create a reminder">
      {patientItems.length >= MIN_ITEMS_FOR_SEARCH && (
        <PillSearchInput
          placeholder="Search recent medications"
          value={search}
          onChange={setSearch}
          onClear={() => setSearch("")}
        />
      )}
      {searchResults.length ? (
        <div className={styles.list}>
          {searchResults.map(({ key, patientItem, reminder }) => (
            <CreateItemCard
              key={key}
              patientItem={patientItem}
              reminder={reminder}
              onClick={() => handleSelect({ patientItem, reminder })}
            />
          ))}
        </div>
      ) : (
        <DrawerLayout.Error
          title="No matching medications"
          description="Try searching by a different medication or pet name"
          image={errorImages.EmptyResults}
          imageAlt="Nothing found"
        />
      )}
    </ReminderDrawerLayout>
  );
};

export default ReminderItemSelectDrawer;
